import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";

const asCountry = (countryCode: string | null | undefined): CountryCode | undefined => {
  if (!countryCode) return undefined;
  const code = countryCode.toUpperCase();
  try {
    getCountryCallingCode(code as CountryCode);
    return code as CountryCode;
  } catch {
    return undefined;
  }
};

/** National example, grouped the same way `formatNationalInput` groups keystrokes. */
export const phoneInputPlaceholder = (countryCode: string): string => {
  const country = asCountry(countryCode);
  if (!country) return "";
  return getExampleNumber(country, examples)?.formatNational() ?? "";
};

/** Calling code shown beside the flag, e.g. `+52`. */
export const dialCodeForCountry = (countryCode: string): string => {
  const country = asCountry(countryCode);
  if (!country) return "+";
  return `+${getCountryCallingCode(country)}`;
};

/** Digits of a valid example. Used to seed gallery states. */
export const exampleNationalDigits = (countryCode: string): string => {
  const country = asCountry(countryCode);
  if (!country) return "";
  return getExampleNumber(country, examples)?.nationalNumber ?? "";
};

/**
 * Group national digits the way the country's plan does.
 * Extra digits past the plan are dropped, so the field cannot grow past a real number.
 */
export const formatNationalInput = (countryCode: string, raw: string): string => {
  const country = asCountry(countryCode);
  const digits = raw.replace(/\D/g, "");
  if (!country || !digits) return digits;
  let accepted = "";
  let formatted = "";
  for (const digit of digits) {
    if (accepted.length >= 15) break;
    const next = accepted + digit;
    const candidate = new AsYouType(country).input(next);
    const lostTemplate =
      next.length > 3 && !/\D/.test(candidate) && /\D/.test(formatted);
    if (lostTemplate) break;
    accepted = next;
    formatted = candidate;
  }
  return formatted;
};

/**
 * Backspace on a formatting character (the ")" in "(916)") does not change the
 * digits, so reformatting would put it back. Drop the digit just before the cursor.
 */
export const nationalInputAfterEdit = (
  countryCode: string,
  previous: string,
  raw: string,
  cursor: number | null,
): string => {
  const prevDigits = previous.replace(/\D/g, "");
  let nextDigits = raw.replace(/\D/g, "");
  if (nextDigits === prevDigits && raw.length < previous.length) {
    const digitsBefore = raw.slice(0, cursor ?? raw.length).replace(/\D/g, "").length;
    const dropAt = Math.max(0, digitsBefore - 1);
    nextDigits = prevDigits.slice(0, dropAt) + prevDigits.slice(dropAt + 1);
  }
  return formatNationalInput(countryCode, nextDigits);
};

/** True when the national number is a valid number for that country. */
export const isNationalPhoneValid = (countryCode: string, value: string): boolean => {
  const country = asCountry(countryCode);
  const digits = value.replace(/\D/g, "");
  if (!country || !digits) return false;
  return parsePhoneNumberFromString(digits, country)?.isValid() ?? false;
};

/** Dialable ISO2 codes. This is not the transfer catalog. */
export function phoneCountryCodes(): string[] {
  return [...getCountries()];
}

/**
 * Identity country wins when it is dialable. Otherwise the role country:
 * origin for a sender, destination for a recipient.
 * List order is not a country. US is the last resort.
 */
export function initialPhoneCountry(input: {
  countries: string[];
  identityCountry?: string | null;
  roleCountry?: string | null;
}): string {
  const listed = (code?: string | null) => {
    const value = code?.trim().toUpperCase();
    return value && input.countries.includes(value) ? value : undefined;
  };
  return listed(input.identityCountry) ?? listed(input.roleCountry) ?? "US";
}

/** Payer writes the number, so the fallback is the corridor origin. */
export function fundRequestPhoneRoleCountry(config?: {
  defaultCorridor?: { originCountryCode?: string | null } | null;
}): string | undefined {
  const code = config?.defaultCorridor?.originCountryCode?.trim();
  return code || undefined;
}
